import { cva } from '@/styled-system/css';

export const tabsTabRecipe = cva({
  base: {
    'display': 'flex',
    'alignItems': 'center',
    'whiteSpace': 'nowrap',
    'paddingBlock': 'Tabs.padding.vert',
    'paddingInline': 'Tabs.padding.horz',
    'position': 'relative',
    'overflow': 'hidden',
    'transition': {
      _motionReduce: 'none',
      _motionSafe:
        'box-shadow 200ms ease, background-color 200ms ease, color 200ms ease, flex-shrink 200ms ease',
    },
    'borderStartStartRadius': 'Tabs.tab',
    'borderStartEndRadius': 'Tabs.tab',
    'marginInlineStart': 'Tabs.offsetBorder',
    'textStyle': 'body',
    'focusOutline': true,
    '&:first-child': {
      marginInlineStart: 0,
    },
    'zIndex': {
      _focusVisible: 3,
    },
  },
  variants: {
    appearance: {
      default: {
        _after: {
          position: 'absolute',
          content: '""',
          background:
            'linear-gradient(90deg, {colors.bg.invisible} 0%, {colors.Tabs.default.bg.tab.default} 75%, {colors.Tabs.default.bg.tab.default})',
          display: 'block',
          height: 'calc(100% - {spacing.150})',
          insetInlineEnd: '0',
          insetBlockStart: '{spacing.25}',
          width: '{spacing.default}',
        },
      },
      file: {
        borderStyle: 'solid',
        borderWidth: 'Tabs.default',
        borderColor: { base: 'Tabs.file.border.default' },
        backgroundColor: { base: 'Tabs.file.bg.tab.default' },
      },
    },
    fluid: {
      true: {
        flexBasis: 0,
        flexGrow: 1,
        justifyContent: 'center',
      },
      false: {
        flexBasis: 'auto',
        flexGrow: 0,
        justifyContent: 'flex-start',
      },
    },
    disabled: {
      true: {
        opacity: 'disabled',
      },
      false: {
        cursor: 'pointer',
      },
    },
    selected: {
      true: {
        flexShrink: 0,
        zIndex: 2,
      },
      false: {},
    },
  },
  compoundVariants: [
    {
      appearance: 'default',
      selected: true,
      css: {
        color: { base: 'Tabs.default.label.selected.default' },
        _before: {
          display: 'block',
          content: '""',
          position: 'absolute',
          insetBlockEnd: 0,
          insetInlineStart: 0,
          width: '100%',
          background: 'Tabs.default.bg.indicator.active',
          height: 'Tabs.indicator',
          borderStartStartRadius: 'Tabs.indicator',
          borderStartEndRadius: 'Tabs.indicator',
        },
      },
    },
    {
      appearance: 'default',
      selected: true,
      disabled: false,
      css: {
        color: {
          _active: 'Tabs.default.label.selected.pressed',
        },
      },
    },
    {
      appearance: 'default',
      selected: false,
      css: {
        color: { base: 'Tabs.default.label.default' },
        backgroundColor: { base: 'Tabs.default.bg.tab.default' },
        borderColor: { base: 'Tabs.default.border.tab.default' },
      },
    },
    {
      appearance: 'default',
      selected: false,
      disabled: false,
      css: {
        color: {
          _hover: 'Tabs.default.label.hover',
          _active: 'Tabs.default.label.pressed',
        },
        backgroundColor: {
          _hover: 'Tabs.default.bg.tab.hover',
          _active: 'Tabs.default.bg.tab.pressed',
        },
        borderColor: {
          _hover: 'Tabs.default.border.tab.hover',
          _active: 'Tabs.default.border.tab.pressed',
        },
      },
    },
    {
      appearance: 'file',
      selected: true,
      css: {
        'color': { base: 'Tabs.file.label.default' },
        'overflow': 'hidden',
        'borderBlockEndColor': 'transparent.invisible',
        'borderInlineStartWidth': 'Tabs.default',
        'borderInlineWidth': 'Tabs.default',
        '_before': {
          display: 'block',
          content: '""',
          position: 'absolute',
          inset: '0',
          borderStartStartRadius: 'calc({radii.Tabs.tab} - {borderWidths.Tabs.default})',
          borderStartEndRadius: 'calc({radii.Tabs.tab} - {borderWidths.Tabs.default})',
          backgroundImage:
            'linear-gradient(180deg, {colors.Tabs.default.bg.indicator.active} 0, {colors.Tabs.default.bg.indicator.active} 100%, {colors.bg.invisible} 100%)',
          backgroundSize: '100% {sizes.Tabs.indicator}',
          backgroundPosition: 'top left',
          backgroundRepeat: 'no-repeat',
        },
        '&:first-child': {
          overflow: 'visible',
          _after: {
            display: 'block',
            content: '""',
            position: 'absolute',
            insetInlineStart: '{spacing.negative-25}',
            insetBlockEnd: '{spacing.negative-25}',
            height: '{borderWidths.Tabs.default}',
            width: '{borderWidths.Tabs.default}',
            backgroundColor: 'Tabs.file.border.default',
          },
        },
      },
    },
    {
      appearance: 'file',
      selected: true,
      disabled: false,
      css: {
        color: {
          _active: 'Tabs.file.label.pressed',
        },
      },
    },
    {
      appearance: 'file',
      selected: false,
      css: {
        color: { base: 'Tabs.file.label.default' },
        _after: {
          position: 'absolute',
          content: '""',
          background:
            'linear-gradient(90deg, {colors.bg.invisible} 0%, {colors.Tabs.file.bg.tab.default} 75%, {colors.Tabs.file.bg.tab.default})',
          backgroundPosition: 'right top',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '{spacing.default} 100%',
          // Because of the outer border, we need to subtract the border width from the radius for it to look correct
          borderStartStartRadius: 'calc({radii.Tabs.tab} - {borderWidths.Tabs.default})',
          borderStartEndRadius: 'calc({radii.Tabs.tab} - {borderWidths.Tabs.default})',
          display: 'block',
          inset: '0',
        },
      },
    },
    {
      appearance: 'file',
      selected: false,
      disabled: false,
      css: {
        color: {
          _hover: 'Tabs.file.label.hover',
          _active: 'Tabs.file.label.pressed',
        },
        zIndex: {
          _active: 3,
        },
        borderColor: {
          _hover: 'Tabs.file.border.hover',
          _active: 'Tabs.file.border.pressed',
        },
        backgroundColor: {
          _hover: 'Tabs.file.bg.tab.hover',
          _active: 'Tabs.file.bg.tab.pressed',
        },
        _hover: {
          _after: {
            background:
              'linear-gradient(90deg, {colors.bg.invisible} 0%, {colors.Tabs.file.bg.tab.hover} 75%, {colors.Tabs.file.bg.tab.hover})',
            backgroundPosition: 'right top',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '{spacing.default} 100%',
            // Because of the outer border, we need to subtract the border width from the radius for it to look correct
            borderStartStartRadius: 'calc({radii.Tabs.tab} - {borderWidths.Tabs.default})',
            borderStartEndRadius: 'calc({radii.Tabs.tab} - {borderWidths.Tabs.default})',
          },
        },
        _active: {
          _after: {
            background:
              'linear-gradient(90deg, {colors.bg.invisible} 0%, {colors.Tabs.file.bg.tab.active} 75%, {colors.Tabs.file.bg.tab.active})',
            backgroundPosition: 'right top',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '{spacing.default} 100%',
            // Because of the outer border, we need to subtract the border width from the radius for it to look correct
            borderStartStartRadius: 'calc({radii.Tabs.tab} - {borderWidths.Tabs.default})',
            borderStartEndRadius: 'calc({radii.Tabs.tab} - {borderWidths.Tabs.default})',
          },
        },
      },
    },
  ],
});
